import { createFileRoute } from '@tanstack/react-router';
import { type ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import { isErrorCode } from '@/api/errors';
import { AlertBox } from '@/common/AlertBox';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import { NoteInput } from '@/common/NoteInput';
import { NoteItem } from '@/common/NoteItem';
import { LoadMoreWrapper, TimelineWrapper } from '@/common/timelineStyles';
import { WBSButton } from '@/common/WBSButton';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { GroupHeaderWrapper, Title } from '~/styles/layoutElements';

// A leaf: the tab has no subnav and no create/edit screen.
export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/group-notes',
)({
  component: RouteComponent,
});

function RouteComponent(): ReactElement {
  const { t } = useTranslation();
  const { groupName } = Route.useParams();
  const { me } = useCurrentUser();
  const [note, setNote] = useState('');
  const [added, setAdded] = useState(false);

  // The slug -> id hop, as every group tab does it.
  const { data: group, isError: groupFailed } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });
  // qraft evaluates parameters before options, so `enabled` cannot guard
  // the object: 0 stands in until the slug resolves.
  const groupId = group?.id ?? 0;

  // No explicit `limit`: the shared default of 10 is the page size.
  const {
    data,
    isError: notesFailed,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = api.listGroupNotes.useInfiniteQuery(
    { path: { group_id: groupId } },
    {
      enabled: groupId > 0,
      initialPageParam: { query: { offset: 0 } },
      // `PaginatedResponse` carries no `hasMore`; derive it from the count.
      getNextPageParam: (lastPage, allPages) => {
        const loaded = allPages.reduce((n, page) => n + page.data.length, 0);
        return loaded < lastPage.count
          ? { query: { offset: loaded } }
          : undefined;
      },
    },
  );

  const {
    mutateAsync: createNote,
    isPending: creating,
    error: createFailed,
  } = api.createGroupNote.useMutation();
  const { mutateAsync: deleteNote, error: deleteFailed } =
    api.deleteGroupNote.useMutation();

  const handleAddNote = async (): Promise<void> => {
    setAdded(false);
    try {
      await createNote({
        path: { group_id: groupId },
        body: { content: note },
      });
      // Cleared before the refetch is awaited so the Add Note button is not
      // live over content that has already been written.
      setNote('');
      setAdded(true);
      await api.listGroupNotes.invalidateQueries();
    } catch {
      // Rendered from `failure` below.
    }
  };

  const handleDeleteNote = async (noteId: number): Promise<void> => {
    setAdded(false);
    try {
      await deleteNote({ path: { group_id: groupId, note_id: noteId } });
    } catch {
      // Rendered from `failure` below. `NoteItem` has already faded the row out.
    }
    // On both paths, so the list is the server's answer rather than ours.
    await api.listGroupNotes.invalidateQueries();
  };

  const items = data?.pages.flatMap((page) => page.data) ?? [];
  // A 403 and a 404 collapse into one screen, and an error only counts when
  // we hold no data: a failed Load More must not hide notes already shown.
  const refused =
    (groupFailed && group === undefined) || (notesFailed && data === undefined);
  const pending = !refused && (group === undefined || data === undefined);
  const failure = createFailed ?? deleteFailed;

  return (
    <div>
      <GroupHeaderWrapper>
        <Title>{t('sidebar.group-notes')}</Title>
      </GroupHeaderWrapper>
      {refused ? (
        <AlertBox variant="error">{t('wbs.page-not-found')}</AlertBox>
      ) : pending ? (
        <LoadingSpinner centered />
      ) : (
        <>
          {added && (
            <AlertBox variant="success">
              {t('groups.group-note-added-successfully')}
            </AlertBox>
          )}
          {failure && (
            // The 422 arm is unreachable from this UI (`NoteInput` disables Add
            // Note on empty text) but is wired for a direct submit.
            <AlertBox variant="error">
              {isErrorCode(failure, 'validation_error')
                ? t('groups.please-enter-group-note')
                : t('wbs.something-went-wrong-try')}
            </AlertBox>
          )}
          <NoteInput
            value={note}
            onChange={setNote}
            onSubmit={() => {
              void handleAddNote();
            }}
            userName={me?.display_name ?? ''}
            placeholder={t('groups.add-a-note-about-this')}
            isSubmitting={creating}
          />
          {items.length === 0 ? (
            // An empty list would read as a broken screen.
            <AlertBox variant="info">{t('wbs.no-results-found')}</AlertBox>
          ) : (
            <TimelineWrapper>
              {items.map((item) => (
                <NoteItem
                  key={item.id}
                  name={item.author_name ?? t('actions.deleted-user')}
                  content={item.content}
                  createdAt={item.created_at}
                  authorId={item.author_id}
                  avatarUrl={item.avatar_url}
                  onDelete={() => {
                    void handleDeleteNote(item.id);
                  }}
                />
              ))}
              {hasNextPage && (
                <LoadMoreWrapper>
                  <WBSButton
                    wide
                    alternate
                    isLoading={isFetchingNextPage}
                    onClick={() => {
                      void fetchNextPage();
                    }}
                  >
                    {t('wbs.load-more')}
                  </WBSButton>
                </LoadMoreWrapper>
              )}
            </TimelineWrapper>
          )}
        </>
      )}
    </div>
  );
}
